import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { MapPin, Loader2, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MIN_QUERY = 3;
const DEBOUNCE_MS = 300;

/**
 * AddressAutocomplete
 *
 * Reusable, accessible address input that proxies queries through
 * `GET /api/geo/autocomplete` (Nominatim server-side). No secret credentials
 * are exposed to the browser. No customer PII is sent to the provider —
 * only the typed address string.
 *
 * Props:
 *   value:            current address string (controlled)
 *   onChange(patch):  called with { address, place_id?, lat?, lng?, manual }
 *                     — `manual: true` means the customer typed and did not
 *                     select a suggestion.
 *   unit:             current unit/suite string (controlled, optional)
 *   onUnitChange(v):  called with unit string
 *   label:            visible label text
 *   idPrefix:         unique DOM id prefix (required — used for ARIA wiring)
 *   error:            optional field-level error string to display and announce
 *   testId:           optional data-testid prefix (defaults to idPrefix)
 *   placeholder:      optional input placeholder
 */
const AddressAutocomplete = ({
  value,
  onChange,
  unit,
  onUnitChange,
  label,
  idPrefix,
  error,
  testId,
  placeholder,
  required = false,
}) => {
  const inputId = useMemo(() => `${idPrefix}-input`, [idPrefix]);
  const unitId = useMemo(() => `${idPrefix}-unit`, [idPrefix]);
  const listboxId = useMemo(() => `${idPrefix}-listbox`, [idPrefix]);
  const errorId = useMemo(() => `${idPrefix}-error`, [idPrefix]);
  const helpId = useId();
  const tid = testId || idPrefix;

  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const skipNextFetch = useRef(false);

  const fetchSuggestions = useCallback(async (q) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const url = `${API}/api/geo/autocomplete?q=${encodeURIComponent(q)}&limit=6`;
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error('bad status');
      const data = await res.json();
      const list = Array.isArray(data?.results) ? data.results : [];
      // Dedup by place_id — Nominatim occasionally returns the same OSM way
      // under multiple query variants which crashes React's key uniqueness.
      const seen = new Set();
      const deduped = list.filter((r) => {
        const k = r?.place_id;
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      setSuggestions(deduped);
      setOpen(deduped.length > 0);
      setHighlight(deduped.length > 0 ? 0 : -1);
    } catch (e) {
      if (e.name !== 'AbortError') {
        setSuggestions([]);
        setOpen(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (manualMode) return;
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = (value || '').trim();
    if (q.length < MIN_QUERY) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(q), DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value, manualMode, fetchSuggestions]);

  const handleInputChange = (e) => {
    const next = e.target.value;
    setSelectedIndex(-1);
    onChange({ address: next, place_id: null, lat: null, lng: null, manual: manualMode });
  };

  const handleSelect = (idx) => {
    const s = suggestions[idx];
    if (!s) return;
    skipNextFetch.current = true;
    setOpen(false);
    setSelectedIndex(idx);
    onChange({
      address: s.formatted_address,
      place_id: s.place_id || null,
      lat: s.lat ?? null,
      lng: s.lng ?? null,
      manual: false,
    });
    // Return focus to input for continued keyboard workflow.
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === 'ArrowDown' && (value || '').trim().length >= MIN_QUERY) {
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
        break;
      case 'Enter':
        if (highlight >= 0) {
          e.preventDefault();
          handleSelect(highlight);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const toggleManual = () => {
    setManualMode((m) => {
      const next = !m;
      // Preserve typed value across mode toggles; only flip the flag.
      onChange({
        address: value || '',
        place_id: null,
        lat: null,
        lng: null,
        manual: next,
      });
      if (next) {
        setOpen(false);
        setSuggestions([]);
      }
      return next;
    });
  };

  const activeDescendant = highlight >= 0 ? `${listboxId}-opt-${highlight}` : undefined;
  const selected = selectedIndex >= 0 || (!!value && !manualMode && !open && suggestions.length === 0 && !!value?.match(/,/));

  return (
    <div className="space-y-2" data-testid={`${tid}-wrapper`}>
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={inputId} className="text-[#004C97] font-medium">
          {label}{required && ' *'}
        </Label>
        <button
          type="button"
          onClick={toggleManual}
          className="text-xs text-[#004C97] hover:text-[#F9A825] underline focus:outline-none focus:ring-2 focus:ring-[#F9A825] rounded"
          data-testid={`${tid}-toggle-manual`}
          aria-pressed={manualMode}
        >
          {manualMode ? 'Try address search' : 'Enter address manually'}
        </button>
      </div>
      <div className="relative">
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input
            ref={inputRef}
            id={inputId}
            value={value || ''}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (!manualMode && suggestions.length > 0) setOpen(true);
            }}
            onBlur={() => {
              // Delay close so click-select can still fire on the option.
              setTimeout(() => setOpen(false), 150);
            }}
            placeholder={placeholder || (manualMode ? 'Type the full street address' : 'Start typing an address…')}
            className="pl-9 border-2 focus:border-[#F9A825] transition-colors"
            required={required}
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-activedescendant={activeDescendant}
            aria-describedby={`${helpId} ${error ? errorId : ''}`.trim()}
            aria-invalid={!!error}
            autoComplete="off"
            data-testid={`${tid}-input`}
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F9A825] animate-spin" aria-hidden="true" />
          )}
        </div>
        {open && suggestions.length > 0 && (
          <ul
            id={listboxId}
            ref={listRef}
            role="listbox"
            className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-lg border-2 border-[#004C97]/20 bg-white shadow-xl"
          >
            {suggestions.map((s, idx) => (
              <li
                key={s.place_id || idx}
                id={`${listboxId}-opt-${idx}`}
                role="option"
                aria-selected={highlight === idx}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(idx); }}
                onMouseEnter={() => setHighlight(idx)}
                className={`px-3 py-2 text-sm cursor-pointer flex items-start gap-2 ${
                  highlight === idx ? 'bg-[#FFF8E1] text-[#004C97]' : 'text-gray-700'
                }`}
                data-testid={`${tid}-option-${idx}`}
              >
                <MapPin className="w-4 h-4 mt-0.5 text-[#F9A825] flex-shrink-0" aria-hidden="true" />
                <span className="leading-snug">{s.description}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p id={helpId} className="text-xs text-gray-500">
        {manualMode
          ? 'Manual entry — dispatch will verify this address.'
          : 'Type at least 3 characters. Use ↑/↓ then Enter to select.'}
      </p>
      {error && (
        <p id={errorId} role="alert" className="text-xs text-red-600 flex items-start gap-1" data-testid={`${tid}-error`}>
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </p>
      )}
      {onUnitChange && (
        <div className="pt-1">
          <Label htmlFor={unitId} className="text-xs text-gray-600 font-normal">
            Unit / Suite / Entrance (optional)
          </Label>
          <Input
            id={unitId}
            value={unit || ''}
            onChange={(e) => onUnitChange(e.target.value)}
            placeholder="e.g., Apt 4B · Suite 200 · West entrance"
            className="border-2 focus:border-[#F9A825] transition-colors"
            data-testid={`${tid}-unit`}
            maxLength={120}
          />
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
